export const MAX_EMPTY_PAGE_FOLLOW = 3;

/** Cursor for the next page; "" / null / undefined / whitespace mean there is no next page. */
export const nextCommentCursor = (
  pagingState?: string | null
): string | null => {
  if (typeof pagingState !== 'string') return null;
  const trimmed = pagingState.trim();
  return trimmed ? pagingState : null;
};

/** A page may come back empty (rows filtered out) while more pages exist: follow it, up to a cap. */
export const shouldAutoFollow = (
  dataLength: number,
  pagingState: string | null | undefined,
  emptyStreak: number,
  maxStreak: number = MAX_EMPTY_PAGE_FOLLOW
): boolean =>
  dataLength === 0 &&
  nextCommentCursor(pagingState) !== null &&
  emptyStreak < maxStreak;

export interface ICommentPage<T> {
  data: T[];
  hasMore: boolean;
  pagingState?: string | null;
}

/**
 * Fetches a page and, while it is empty but has a cursor, fetches the next one
 * (at most `maxStreak` extra pages). Returned hasMore is false when no cursor remains.
 */
export const fetchFollowingEmptyPages = async <T>(
  fetchPage: (cursor: string) => Promise<ICommentPage<T>>,
  cursor: string,
  maxStreak: number = MAX_EMPTY_PAGE_FOLLOW
): Promise<{ data: T[]; hasMore: boolean; pagingState: string }> => {
  let current = cursor;
  let streak = 0;
  let res = await fetchPage(current);
  while (shouldAutoFollow(res.data?.length ?? 0, res.pagingState, streak, maxStreak)) {
    streak += 1;
    current = nextCommentCursor(res.pagingState) as string;
    res = await fetchPage(current);
  }
  const next = nextCommentCursor(res.pagingState);
  return {
    data: res.data ?? [],
    hasMore: !!res.hasMore && next !== null,
    pagingState: next ?? '',
  };
};
